/**
 * Filter Button
 * General purpose record filter
 */
import { ClickAwayListener, Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isAfter, isWithinInterval, isBefore, isSameDay } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type {
  DataTableColumn,
  DataTableRecord,
} from '@components/DataTable/DataTable';
import Icon from '@components/Icon/Icon';
import FilterModalContent from '@components/Modal/Content/FilterModalContent';

import { useModal } from '@hooks/useModal';

import { FilterDiv, LabelFormat, NumFilterApplied } from './FilterButton.style';

export type FilterItems = {
  date: { name: string; to: Date | null; from: Date | null };
  boolean: Record<string, boolean | null>;
  others: Record<string, Record<string, boolean>>;
};

export type FilterMode = 'modal' | 'popper';

const messages = defineMessages({
  filter: {
    id: 'filterButton.filter',
    defaultMessage: 'filter',
  },
});

// Create filters array to pass to modal
const createFilters = (
  filterItems: DataTableRecord[],
  fields: DataTableColumn[]
): FilterItems => {
  const filters: FilterItems = {
    date: { name: '', to: null, from: null },
    boolean: {},
    others: {},
  };

  // Create entry for every field
  fields.forEach((f) => {
    if (f.style === 'date') {
      filters.date = { name: f.name, to: null, from: null };
    } else if (f.style === 'boolean') {
      filters.boolean[f.name] = null;
    } else {
      filters.others[f.name] = {};
    }
  });

  // Populate options
  Object.entries(filters.others).forEach(([key, record]) => {
    filterItems.forEach((i: DataTableRecord) => {
      const value = i[key];
      if (typeof value === 'string' && value) {
        record[value] = false;
      }
    });
  });
  return filters;
};

const FilterButton: React.FC<{
  items: DataTableRecord[];
  fields: DataTableColumn[];
  onChange?: (value: DataTableRecord[]) => void;
  mode?: FilterMode;
}> = ({ items = [], fields = [], onChange, mode = 'modal' }) => {
  const { openModal, setContent } = useModal();
  const [filterData, setFilterData] = useState(items);
  const [filters, setFilters] = useState(createFilters(items, fields));
  const [numFilters, setNumFilters] = useState(0);
  const [filterModal, setFilterModal] = useState<React.ReactNode | null>(null);
  const { formatMessage } = useIntl();
  const { palette, shadows } = useTheme();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const handleOpenPopper = () => setPopperOpen(true);
  const handleClosePopper = () => setPopperOpen(false);

  useEffect(() => {
    setFilterData(items);
    setFilters(createFilters(items, fields));
    setNumFilters(0);
  }, [fields, items]);

  // Filter items array based on filter array
  const filteredItems = (
    nonFilteredItems: DataTableRecord[],
    filters: FilterItems
  ) => {
    let filteredByCheckbox = nonFilteredItems.flatMap(
      (item: DataTableRecord) => {
        // Matched items
        const matchedItems = Object.keys(item).filter((key) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const record = filters.others[key] ?? {};

          const categoryCount = Object.keys(record).reduce(
            (acc, curr) => acc + (record[curr] ? 1 : 0),
            0
          );

          const itemVal = item[key];
          if (typeof itemVal !== 'string') {
            return false;
          }
          return !record[itemVal] && categoryCount !== 0;
        });
        return matchedItems.length > 0 ? [] : [item];
      }
    );

    filteredByCheckbox = filteredByCheckbox.filter((item: DataTableRecord) => {
      if (Object.entries(filters.boolean).every(([, v]) => v === null)) {
        return true;
      }
      const recordKeys = Object.entries(item)
        .filter(([, v]) => typeof v === 'boolean')
        .map(([k]) => k);
      return recordKeys.some((key) => item[key] === filters.boolean[key]);
    });

    filteredByCheckbox = filteredByCheckbox.filter((item: DataTableRecord) => {
      if (!filters.date.from && !filters.date.to) {
        return true;
      }

      const matchingItem = item[filters.date.name];
      if (!(matchingItem instanceof Date)) {
        return true;
      }

      if (!filters.date.from && filters.date.to) {
        return (
          isBefore(matchingItem, filters.date.to) ||
          isSameDay(matchingItem, filters.date.to)
        );
      }
      if (!filters.date.to && filters.date.from) {
        return (
          isAfter(matchingItem, filters.date.from) ||
          isSameDay(matchingItem, filters.date.from)
        );
      }

      if (filters.date.from && filters.date.to) {
        return (
          isWithinInterval(matchingItem, {
            start: filters.date.from,
            end: filters.date.to,
          }) ||
          isSameDay(matchingItem, filters.date.to) ||
          isSameDay(matchingItem, filters.date.from)
        );
      }

      return true;
    });
    return filteredByCheckbox;
  };

  // Count filters
  const countFilters = (filterItems: FilterItems) => {
    setNumFilters(
      Object.keys(filterItems)
        .map((category) => {
          if (category === 'date') {
            return filterItems.date.from || filterItems.date.to ? 1 : 0;
          }
          let count = 0;
          for (const otherCategory of Object.keys(filterItems.others)) {
            const record = filterItems.others[otherCategory];
            count += Object.keys(record).filter(
              (selection) => record[selection]
            ).length;
          }
          return count;
        })
        .reduce((acc, curr) => acc + curr, 0)
    );
  };

  // Update filters, send new items to parent
  const updateFilters = (updatedFilterItems: FilterItems) => {
    setFilters(updatedFilterItems);
    countFilters(updatedFilterItems);
    onChange?.(filteredItems(filterData, updatedFilterItems));
    if (mode === 'modal') {
      setContent({
        children: (
          <FilterModalContent
            fields={fields}
            filters={updatedFilterItems}
            inputCallback={(value) => updateFilters(value)}
          />
        ),
      });
    } else {
      setFilterModal(
        <FilterModalContent
          fields={fields}
          filters={updatedFilterItems}
          inputCallback={(value) => updateFilters(value)}
        />
      );
    }
  };

  const handleClick = () => {
    if (mode === 'modal') {
      openModal({
        width: 400,
        props: {
          height: '300px',
        },
        children: null,
      });
    } else {
      if (!popperOpen) {
        handleOpenPopper();
      }
    }
    updateFilters(filters);
  };

  return (
    <>
      <FilterDiv
        hasFilters={numFilters !== 0}
        onClick={handleClick}
        ref={anchorRef}
      >
        <Icon
          clickable
          color="text.primary"
          name="sliders"
          style={{ marginRight: 1 }}
        />
        <LabelFormat hasFilters={numFilters !== 0}>
          {formatMessage(messages.filter)}
        </LabelFormat>
        {numFilters !== 0 && <NumFilterApplied>{numFilters}</NumFilterApplied>}
      </FilterDiv>
      <Popper
        anchorEl={anchorRef.current}
        disablePortal
        open={popperOpen}
        placement="bottom-end"
        style={{
          minWidth: '400px',
          zIndex: 1310,
          border: `2px solid ${palette.divider}`,
          borderRadius: '5px',
          boxShadow: `${shadows[9]}`,
        }}
      >
        <ClickAwayListener onClickAway={handleClosePopper}>
          <div>{filterModal}</div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default FilterButton;
